import angular from "angular";

import { Calculations, Ajax, Calculation, DocumentFlowItem, AccountingData, Reports, CalculationResultLogic, DateRange, InputEnumOption, Translations, Language, kevaEmploymentRegCodes } from "@salaxy/core";
import { InvoicesService, ReportsService, SessionService, UiCrudHelpers, UiHelpers } from "../../services";
import { Calculator2019Controller } from "./Calculator2019Controller";

/**
 * Controller for repairing paid calculations.
 */
export class CalcRepairController extends Calculator2019Controller {

  /**
   * For NG-dependency injection
   */
  public static $inject = [
    "Calculations",
    "UiHelpers",
    "UiCrudHelpers",
    "$location",
    "$routeParams",
    "SessionService",
    "ReportsService",
    "InvoicesService",
    "AjaxNg1",
    "Reports"
  ];

  /** Data reader for the accounting diffa data. */
  public accountingDiffDataReader = {
    /** Function to read data into given array */
    read: (target: string, ruleSet: string, arr: AccountingData[]): Promise<void> => {
      if (this.accountingDiffData) {
        arr.push(this.accountingDiffData);
        return Promise.resolve();
      } else {
        return Promise.resolve();
      }
    },
    /** Optional label for data export */
    exportLabel: null,
    /** Indicates if the export is not possible */
    disabled: () => !this.accountingDiffData,
    /* Indicates if row is expandable */
    showExpand: true,
    /* Indicates if rows exists*/
    hasRows: () => !this.accountingDiffData?.ledgerAccounts
  };

  public excludeDiffPaths = ["workflow", "id", "createdAt", "updatedAt", "isReadOnly", "result.totals", "result.irRows", "result.employerCalc", "result.workerCalc", "result.rows", "result.validation"];

  /** Calc reports type */
  public shared = {
    reportType: "salarySlip"
  };

  private _loading = {
    accountingDiffData: false,
  };

  private _pendingChanges = {
    accountingDiffData: false,
    accountingDiffTargetId: "default"
  };

  private _accountingDiffData: AccountingData;

  private _diffCalc: Calculation = {};

  private _latestCurrent: Calculation;

  private _latestBase: Calculation;

  private _base: Calculation;
  private _documentFlow: DocumentFlowItem;
  private _dateRange: DateRange;

  private _baseCfa: any = { net: 0, tax:0, pension:0, service:0, fee:0, total:0 };

  private _currentCfa: any = { net: 0, tax:0, pension:0, service:0, fee:0, total:0 };

  /**
   * Creates a new CalculationsProController
   */
  constructor(
    private repairApi: Calculations,
    private uiHelpersApi: UiHelpers,
    uiCrudHelpers: UiCrudHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    sessionService: SessionService,
    reportsService: ReportsService,
    invoicesService: InvoicesService,
    ajax: Ajax,
    private reportsApi: Reports,
  ) {
    super(repairApi, uiHelpersApi, uiCrudHelpers, $location, $routeParams, sessionService, reportsService, invoicesService, ajax);
  }

  /** Initialize */
  public $onInit() {
    super.$onInit();
  }

  /**
   * Finalize the repair and replace the original version with the new one.
   */
  public commitRepair() {
    return this.uiHelpers.showConfirm( "Korvaa alkuperäinen laskelma?",
      "Tämä korjattu versio laskelmasta korvaa alkuperäisen laskelman. " +
      "Samalla päivitetään tiedot tulorekisteri-ilmoituksiin ja laskuihin. " +
      "Korjausta ei voi perua, mutta voit aina korjata viimeisimmän version laskelmasta.",
       "Korvaa alkuperäinen", "Peruuta").then((result) => {
        if (result) {
          const loading = this.uiHelpersApi.showLoading("SALAXY.UI_Terms.wait");
          return this.repairApi.commitRepair(this.current).then(() => {
            loading.dismiss();
            this.uiHelpers.showAlert("Laskelma korvattu onnistuneesti.");
            this.$location.path("/calc/repairs");
          });
        }
    });
  }

  /**
   * Update repair
   */
  public saveRepair(): Promise<Calculation> {
    return this.repairApi.saveRepair(this.current).then( (saved) => {
      this.setCurrent(saved);
      return this.current;
    });
  }

  /**
   * Recalculate repair
   */
    public recalculateRepair(): Promise<Calculation> {
      return this.repairApi.recalculateRepair(this.current).then( (saved) => {
        this.setCurrent(saved);
        return this.current;
      });
    }

  /**
   * Select taxcard for the repaired calc.
   */
  public selectTaxcard() {
    return this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/TaxcardSelect.html",
      [],
      {
        title: "SALAXY.UI_Terms.select",
        options:  {$filter: "data/employmentId eq '" + this.current.worker.employmentId + "' and (startAt le " + this.current.workflow.salaryDate + "Z and endAt ge " + this.current.workflow.salaryDate + "Z)"},
        okButtonText: "SALAXY.UI_Terms.ok",
        cancelButtonText: "SALAXY.UI_Terms.cancel",
      }).then((result) => {
        if (result.action === "ok" && result.item.length > 0) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          this.current.worker.tax.taxcardId = result.item[0].id;
          return this.recalculate().then(() => {
              loading.dismiss();
          });
        }
        else
        {
          return;
        }
      });
  }

  /** Original calculation */
  public get base() {
    if (!this._base) {
      if ((this.current?.workflow as any)?.originalId) {
        this._base = {};
        this.repairApi.getSingle((this.current?.workflow as any)?.originalId).then((calc) => {
          angular.copy(calc, this._base);
        });
      }
    }
    return this._base?.info ? this._base : null;
  }

  /** Document flow for the current calculation */
  public get documentFlow() {
    if (!this._documentFlow) {
      if (this.base?.id) {
        this._documentFlow = { item: null, children: null };
        this.repairApi.getDocumentFlow(this.base.id).then((data) => {
          angular.copy(data, this._documentFlow);
        });
      }
    }
    return this._documentFlow;
  }

  /** Calculation total result diff between original and current calculation. */
  public get diffCalc() {
    this.checkCalculationChange();
    return this._diffCalc;
  }

  /** Accounting data diff between original and current calculation. */
  public get accountingDiffData() {
    this.checkCalculationChange();
    this.loadAccountingDiffData();
    return this._accountingDiffData;
  }

  /** Change accounting data target */
  public onTargetChange(targetId: string = "default") {
    this._pendingChanges.accountingDiffData = true;
    this._pendingChanges.accountingDiffTargetId = targetId;
    this.loadAccountingDiffData();
  }

  /** Returns cfa data for base. */
  public get baseCfa() {
    if (this.base) {
      this.calculateCfa(this.base, this._baseCfa);
    }
    return this._baseCfa;
  }

    /** Returns cfa data for current. */
  public get currentCfa() {
    if (this.current) {
      this.calculateCfa(this.current, this._currentCfa);
    }
    return this._currentCfa;
  }

  /** Getter and setter for Calculation daterange in compatible format for new DateRange component. */
  public get dateRange(): DateRange {
    if (!this.current) {
      return null;
    }
    if (!this._dateRange) {
      this._dateRange = {
        start: this.current.info.workStartDate,
        end: this.current.info.workEndDate,
        daysCount: this.current.framework.numberOfDays,
      };
    }
    return this._dateRange;
  }
  public set dateRange(value: DateRange) {
    this._dateRange = value;
  }

  /**
   * Called when the date range changes.
   */
  public dateRangeChange() {
    if (!this.dateRange || !this.current) {
      return;
    }
    this.current.info.workStartDate = this.dateRange.start;
    this.current.info.workEndDate = this.dateRange.end;
    this.current.framework.numberOfDays = this.dateRange.daysCount;
  }

  /**
   * Returns valid employment reg codes for the selected pensions company.
   * @returns Options for the employment reg codes.
   */
  public getEmploymentRegCodeOptions(): Array<InputEnumOption> {
    return this.uiHelpers.cache(this, "employmentRegCodes",
    () => kevaEmploymentRegCodes.filter((x) => x.pensionCompany === this.current.result.responsibilities.pensionCompany).map((regCode) =>
      ({ value: regCode.code, text: Translations.getLanguage() === Language.Sv ? regCode.labelSv : regCode.labelFi })),
    () => this.current.result.responsibilities.pensionCompany);
  }

  /** Load accounting data and calculate diff. */
  private loadAccountingDiffData() {
    if (this._pendingChanges.accountingDiffData) {
      if (!this._loading.accountingDiffData) {
        this._loading.accountingDiffData = true;
        const load = () => {
          this._pendingChanges.accountingDiffData = false;
          const promises = [this.base, this.current].map((x) => this.reportsApi.getAccountingDataForCalculations([x], this._pendingChanges.accountingDiffTargetId, null));
          return Promise.all(promises).then((accountingReports) => {
            if (accountingReports && accountingReports.length == 2) {
              this._accountingDiffData = this._accountingDiffData ?? {};
              angular.copy(this.diffAccountingData(accountingReports[0], accountingReports[1]), this._accountingDiffData);
            }
            return this._accountingDiffData;
          });
        };

        load().then(() => {
          if (this._pendingChanges.accountingDiffData) {
            load();
          }
          else {
            this._loading.accountingDiffData = false;
          }
        });
      }
    }
  }

  private diffAccountingData(originalAccountingData: AccountingData, modifiedAccountingData: AccountingData): AccountingData {
    // Leave only top level.
    originalAccountingData.ledgerAccounts.forEach((x) => x.entries = []);
    originalAccountingData.summary = {};
    modifiedAccountingData.ledgerAccounts.forEach((x) => x.entries = []);
    modifiedAccountingData.summary = {};
    const result = modifiedAccountingData;

    originalAccountingData.ledgerAccounts.forEach((orig) => {
      const modified = result.ledgerAccounts.find((x) => x.accountNumber === orig.accountNumber && x.accountGrouping == orig.accountGrouping);
      if (modified) {
        (modified as any).saldo = modified.saldo - orig.saldo;
      } else {
        const removedAccount = angular.copy(orig);
        (removedAccount as any).saldo = 0.0 - orig.saldo;
        result.ledgerAccounts.push(removedAccount);
      }
    });
    result.ledgerAccounts.forEach((x) => x.entries =
      [
        {
          id: null,
          entryDate: null,
          description: "Ero",
          isDebit: x.saldo >= 0,
          amount: x.saldo >= 0 ? x.saldo : -1.0 * x.saldo,
          vatPercent: null,
          accountNumber: x.accountNumber,
          accountName: x.accountName,
          accountGrouping: x.accountGrouping,
          dimension: {},
        }
      ]);
    return result;
  }


  private checkCalculationChange() {
    if (this.base && this.current && (!angular.equals(this.current, this._latestCurrent) || !angular.equals(this.base, this._latestBase))) {
      this._latestCurrent = angular.copy(this.current);
      this._latestBase = angular.copy(this.base);

      const diff = angular.copy(this.base);
      diff.result = CalculationResultLogic.diff(this.base.result, this.current.result);
      angular.copy(diff, this._diffCalc);
      this._pendingChanges.accountingDiffData = true;
    }
  }

  private calculateCfa(calc: Calculation, target: any) {
    target.net = calc.result.workerCalc.totalWorkerPayment - calc.result.employerCalc.deductionWorkerSelfPayment;
    target.pension = calc.result.totals.pension - calc.result.employerCalc.deductionPensionSelfPayment;
    target.tax = calc.result.workerCalc.tax + calc.result.employerCalc.socialSecurity + calc.result.workerCalc.taxAtSource +  calc.result.workerCalc.socialSecurity - calc.result.employerCalc.deductionTaxAndSocialSecuritySelfPayment;
    target.service = calc.result.employerCalc.service;
    target.fee = calc.result.employerCalc.palkkaus;
    target.total = target.net + target.pension + target.tax + target.service + target.fee;
  }

}
